import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Collapse,
    Box,
    Typography,
    Chip,
    Grid,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format } from 'date-fns';
import { ProductOrder, OrdersTabProps, ShippingStatus } from '../ProductTypes';
import SearchIcon from '@mui/icons-material/Search';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { toast } from 'react-hot-toast';
import { Timestamp } from 'firebase/firestore';

interface ExpandableRowProps {
    order: ProductOrder;
    onUpdateShippingStatus: (orderId: string, status: ShippingStatus) => Promise<void>;
}

const ShippingStatusIndicator: React.FC<{ 
    currentStatus: ShippingStatus, 
    onStatusChange: (status: ShippingStatus) => void 
}> = ({ currentStatus, onStatusChange }) => {
    const statuses = [
        ShippingStatus.TO_PROCESS,
        ShippingStatus.PROCESSING,
        ShippingStatus.SHIPPED,
        ShippingStatus.DELIVERED
    ];

    const getStatusColor = (status: ShippingStatus) => {
        switch (status) {
            case ShippingStatus.DELIVERED:
                return 'bg-green-500';
            case ShippingStatus.SHIPPED:
                return 'bg-blue-500';
            case ShippingStatus.PROCESSING:
                return 'bg-yellow-500';
            case ShippingStatus.TO_PROCESS:
                return 'bg-gray-500';
            case ShippingStatus.CANCELLED:
                return 'bg-red-500';
            default:
                return 'bg-gray-300';
        }
    };

    const getCurrentIndex = () => {
        return statuses.indexOf(currentStatus);
    };

    return (
        <div className="flex items-center space-x-1">
            {currentStatus === ShippingStatus.CANCELLED ? (
                <div className="flex items-center bg-red-50 px-2 py-1 rounded">
                    <div className="w-2 h-2 rounded-full bg-red-500 mr-2" />
                    <span className="text-sm text-red-700">Cancelled</span>
                </div>
            ) : (
                <div className="flex items-center space-x-2">
                    {statuses.map((status, index) => {
                        const isActive = getCurrentIndex() >= index;
                        const canTransition = getCurrentIndex() + 1 === index;
                        
                        return (
                            <div key={status} className="flex items-center">
                                <button
                                    onClick={() => canTransition ? onStatusChange(status) : null}
                                    className={`
                                        relative w-6 h-6 rounded-full 
                                        ${isActive ? getStatusColor(status) : 'bg-gray-200'}
                                        ${canTransition ? 'cursor-pointer hover:ring-2 ring-offset-2 ring-blue-300' : 'cursor-default'}
                                        transition-all duration-200
                                    `}
                                >
                                    {isActive && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-2 h-2 bg-white rounded-full" />
                                        </div>
                                    )}
                                </button>
                                {index < statuses.length - 1 && (
                                    <div 
                                        className={`w-8 h-0.5 ${
                                            getCurrentIndex() > index ? getStatusColor(status) : 'bg-gray-200'
                                        }`}
                                    />
                                )}
                            </div>
                        );
                    })}
                    <div className="ml-2">
                        <span className="text-sm font-medium text-gray-700">
                            {currentStatus}
                        </span>
                    </div>
                </div>
            )}
            {currentStatus !== ShippingStatus.CANCELLED && currentStatus !== ShippingStatus.DELIVERED && (
                <button
                    onClick={() => onStatusChange(ShippingStatus.CANCELLED)}
                    className="ml-2 text-xs text-red-600 hover:text-red-800"
                >
                    Cancel
                </button>
            )}
        </div>
    );
};

const ExpandableRow: React.FC<ExpandableRowProps> = ({ order, onUpdateShippingStatus }) => {
    const [open, setOpen] = useState(false);

    const getOverallStatus = () => {
        if (order.payment_status === 'Paid' && order.settlement_status === 'Settled') {
            return 'Settled';
        } else if (order.payment_status === 'Paid') {
            return 'Paid';
        }
        return 'Pending';
    };

    const handleStatusChange = async (newStatus: ShippingStatus) => {
        await onUpdateShippingStatus(order.id, newStatus);
    };

    // Safely access order details with fallbacks
    const orderDetails = order.order_details || {};
    const orderItems = orderDetails.items || [];

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{order.id}</TableCell>
                <TableCell>{format(order.createdAt.toDate(), 'PPp')}</TableCell>
                <TableCell>
                    <div>{order.name}</div>
                    <div className="text-sm text-gray-500">{order.email}</div>
                </TableCell>
                <TableCell>₹{(order.amount || 0).toFixed(2)}</TableCell>
                <TableCell>
                    <Chip
                        label={getOverallStatus()}
                        color={
                            getOverallStatus() === 'Settled' 
                                ? 'success' 
                                : getOverallStatus() === 'Paid' 
                                    ? 'primary' 
                                    : 'default'
                        }
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <ShippingStatusIndicator
                        currentStatus={order.shipping_status || ShippingStatus.TO_PROCESS}
                        onStatusChange={handleStatusChange}
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            {/* Order Items Section */}
                            <Typography variant="h6" gutterBottom component="div">
                                Order Items
                            </Typography>
                            <Table size="small" sx={{ marginBottom: 3 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product ID</TableCell>
                                        <TableCell>Quantity</TableCell>
                                        <TableCell>Price</TableCell>
                                        <TableCell>Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orderItems.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{item.productId || '-'}</TableCell>
                                            <TableCell>{item.quantity || 0}</TableCell>
                                            <TableCell>₹{(item.price || 0).toFixed(2)}</TableCell>
                                            <TableCell>₹{(item.totalPrice || 0).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                            Subtotal:
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            ₹{(orderDetails.totalAmount || 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                            Total (inc. charges):
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            ₹{(orderDetails.totalAmountWithPg || 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* Order Type Information */}
                            <Box sx={{ marginBottom: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Order Type
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Item Type: {orderDetails.itemType || '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Item ID: {orderDetails.itemId || '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Payment & Settlement Details Section */}
                            <Typography variant="h6" gutterBottom component="div">
                                Payment & Settlement Details
                            </Typography>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Payment Method</TableCell>
                                        <TableCell>{order.paymentMethod || '-'}</TableCell>
                                        <TableCell component="th" scope="row">Payment Time</TableCell>
                                        <TableCell>
                                            {order.paymentTime ? format(order.paymentTime.toDate(), 'PPp') : '-'}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Platform Fees</TableCell>
                                        <TableCell>₹{(order.platformFees || 0).toFixed(2)}</TableCell>
                                        <TableCell component="th" scope="row">Transfer Amount</TableCell>
                                        <TableCell>₹{(order.transferAmount || 0).toFixed(2)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Settlement ID</TableCell>
                                        <TableCell>{order.settlementId || '-'}</TableCell>
                                        <TableCell component="th" scope="row">Settlement Time</TableCell>
                                        <TableCell>
                                            {order.settlementTime ? format(order.settlementTime.toDate(), 'PPp') : '-'}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Transfer ID</TableCell>
                                        <TableCell>{order.transferId || '-'}</TableCell>
                                        <TableCell component="th" scope="row">Transfer Status</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={order.transfer_status || 'Pending'}
                                                color={order.transfer_status === 'TransferSucceeded' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};



const OrdersTab: React.FC<OrdersTabProps> = ({
    orders,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    providerId,
    updateOrderInState
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    const updateShippingStatus = async (orderId: string, status: ShippingStatus) => {
        try {
            const orderRef = doc(db, 'ServiceProviders', providerId, 'ProductOrders', orderId);
            const updates = {
                shipping_status: status,
                updatedAt: serverTimestamp()
            };
            
            await updateDoc(orderRef, updates);
            
            // Update just this order in the state
            updateOrderInState(orderId, {
                shipping_status: status,
                updatedAt: new Timestamp(Math.floor(Date.now() / 1000), 0)
            });
            
            toast.success('Shipping status updated successfully');
        } catch (error) {
            console.error('Error updating shipping status:', error);
            toast.error('Failed to update shipping status');
        }
    };

    // Filter orders based on search query
    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const searchLower = searchQuery.toLowerCase();
            return (
                order.name?.toLowerCase().includes(searchLower) ||
                order.email?.toLowerCase().includes(searchLower) ||
                order.id?.toLowerCase().includes(searchLower)
            );
        });
    }, [orders, searchQuery]);

    // Sort orders by date (most recent first)
    const sortedOrders = useMemo(() => {
        return [...filteredOrders].sort((a, b) => 
            b.createdAt.toMillis() - a.createdAt.toMillis()
        );
    }, [filteredOrders]);

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Search Bar */}
            <div className="p-4">
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search by customer name, email, or order ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="50px" />
                            <TableCell>Order ID</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Shipping Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedOrders
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((order) => (
                                <ExpandableRow 
                                    key={order.id} 
                                    order={order} 
                                    onUpdateShippingStatus={updateShippingStatus}
                                />
                            ))}
                        {filteredOrders.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No orders found matching your search.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={filteredOrders.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />
        </div>
    );
};

export default OrdersTab; 